import PropTypes from "prop-types";

import { Paper, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { useAuth } from "@/features/authentication";
import { PermissionEnum } from "@/features/permissions";
import { formatNumber } from "@/features/shared";
import {
  DownstreamTransactionLinks,
  ProductVariantNameCell,
  UpstreamTransactionLink,
} from "@/features/ui";

export const ProductVariantsSection = ({
  deliveryOrderProductVariants,
  warehouse,
}) => {
  const { hasPermission } = useAuth();

  if (!deliveryOrderProductVariants?.length) {
    return null;
  }

  const columns = [
    {
      field: "name",
      headerName: "Produk Varian",
      flex: 5,
      renderCell: ({ row: { name, sku } }) => (
        <ProductVariantNameCell
          name={name}
          sku={sku}
          linkToStock
          warehouseId={warehouse?.id}
          warehouseName={warehouse?.name}
        />
      ),
    },
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      flex: 1,

      renderCell: ({ row }) => formatNumber(row.quantity),
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit",
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => row.product.unitOfMeasurement.name,
    },
    {
      field: "salesOrder",
      headerName: "Pesanan",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row: { salesOrder } }) => (
        <UpstreamTransactionLink
          referenceNumber={salesOrder?.referenceNumber}
          quantity={salesOrder?.quantity}
          clickable={hasPermission(PermissionEnum.READ_SALES_ORDERS)}
        />
      ),
    },
    {
      field: "salesInvoices",
      headerName: "Faktur",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.salesInvoices}
          clickable={hasPermission(PermissionEnum.READ_SALES_INVOICES)}
        />
      ),
    },
    {
      field: "salesPayments",
      headerName: "Pembayaran",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.salesPayments}
          clickable={hasPermission(PermissionEnum.READ_SALES_PAYMENTS)}
        />
      ),
    },
    {
      field: "salesReturns",
      headerName: "Retur",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <DownstreamTransactionLinks
          items={row.salesReturns}
          clickable={hasPermission(PermissionEnum.READ_SALES_RETURNS)}
        />
      ),
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
      valueGetter: (_, row) => row.note || "-",
    },
  ];

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle1">Pengiriman Produk Varian</Typography>
        <DataGrid
          getRowHeight={() => "auto"}
          columns={columns}
          rows={deliveryOrderProductVariants}
          disableRowSelectionOnClick
          initialState={{
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
        />
      </Stack>
    </Paper>
  );
};

ProductVariantsSection.propTypes = {
  deliveryOrderProductVariants: PropTypes.array.isRequired,
  warehouse: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
};
